import { useTranslation } from "react-i18next";
import { ShowLoadingScreen } from "components/common/Loading";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card } from "reactstrap";
import { formatDecimalWithComma } from "utils/formatDecimalWithComma";
import {
  AIProposeRequest,
  ApproveUnapproved,
  GetSingleUnapprovedDataSet,
} from "network/wineDataSetAPI";
import PATHS from "constant/paths";
import { ErrorToaster, successToaster } from "components/common/toast";
import handleError from "utils/handleError";
import CountryList from "country-list-with-dial-code-and-flag";
import RequestOperatorPanel from "./RequestOperatorPanel";
import { mediaUrl } from "utils/mediaUrl";

// Dati che arrivano dal ristorante: sempre mostrati in alto.
const FROM_RESTAURANT = [
  ["name"],
  ["winery"],
  ["vintage"],
  ["ean"],
];
// Dati del ristorante mostrati in alto solo se presenti (altrimenti restano da completare).
const OPTIONAL_FROM_RESTAURANT = [
  ["colour"],
  ["region"],
  ["denomination"],
  ["grapes"],
];
// Da completare.
const TO_FILL = [
  ["descriptive", [
    ["description", "wide"],
    ["appearance", "wide"],
    ["aroma", "wide"],
    ["taste", "wide"],
    ["pairing", "wide"],
  ]],
  ["origin", [
    ["country"],
    ["region"],
    ["colour"],
    ["denomination"],
    ["grapes"],
    ["gradation", "unit", "% vol"],
    ["format", "unit", "L"],
    ["terroir", "wide"],
    ["vinification", "wide"],
    ["fermentation", "wide"],
    ["ingredients", "wide"],
  ]],
  ["production", [
    ["bio", "bool"],
    ["natural", "bool"],
    ["biodynamic", "bool"],
    ["have_sulphite", "bool"],
    ["conventional", "bool"],
  ]],
  ["nutrition", [
    ["energy"],
    ["fat", "unit", "g"],
    ["saturated_fat", "unit", "g"],
    ["carbohydrate", "unit", "g"],
    ["sugar", "unit", "g"],
    ["protein", "unit", "g"],
    ["salt", "unit", "g"],
  ]],
];
const IMAGES = [
  ["front_bottle_image"],
  ["back_bottle_image"],
  ["front_label_image"],
  ["back_label_image"],
];

const isEmpty = (v) => v === null || v === undefined || v === "";

const Field = ({ label, children, wide, filled, suggested }) => {
  const { t } = useTranslation("wine");
  return (
  <div className={`wd-field ${wide ? "wd-field-wide" : ""}`}>
    <div className="wd-field-label">
      {label}
      {suggested ? <span className="wd-tag wd-tag-ai">{t("requests.suggested")}</span> : null}
    </div>
    <div className={`wd-field-value ${filled ? "" : "wd-field-empty"}`}>
      {filled ? children : t("requests.toFill")}
    </div>
  </div>
  );
};

const UnapprovedWineDataSingle = () => {
  const { t } = useTranslation("wine");
  const [singleData, setSingleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  async function getSingleData() {
    try {
      const res = await GetSingleUnapprovedDataSet(id);
      setSingleData(res?.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    getSingleData();
  }, [id]);

  const handleApprove = (uuid) => async () => {
    try {
      const response = await ApproveUnapproved(uuid);
      if (response.success) {
        successToaster(response.message || t("requests.approved"));
        navigate(PATHS.WINE_DATASET_UNAPPROVED_LIST);
      }
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
    }
  };

  const askAI = async () => {
    setAsking(true);
    try {
      const res = await AIProposeRequest(singleData.wine_dataset_request_uuid);
      successToaster(res?.message || t("requests.aiSaved"));
      await getSingleData();
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
    } finally {
      setAsking(false);
    }
  };

  if (loading) {
    return <ShowLoadingScreen />;
  }
  if (!singleData) {
    return <div className="content my-4">{t("requests.notFound")}</div>;
  }

  const attrs = singleData.attributes || {};
  const suggested = attrs.suggested_fields || [];
  const shownTop = new Set(FROM_RESTAURANT.map(([k]) => k));
  const topOptional = OPTIONAL_FROM_RESTAURANT.filter(([k]) => !isEmpty(attrs[k]) && !suggested.includes(k));
  topOptional.forEach(([k]) => shownTop.add(k));

  const display = (key, type, unit) => {
    const v = attrs[key];
    if (type === "bool") return v === true ? t("common.yes") : t("common.no");
    if (type === "unit") return `${formatDecimalWithComma(v)}${unit ? ` ${unit}` : ""}`;
    if (key === "vintage") return formatDecimalWithComma(v);
    if (key === "country") return CountryList.findOneByCountryCode(v)?.name || String(v);
    return String(v);
  };
  const filled = (key) => !isEmpty(attrs[key]);

  const missingCount = TO_FILL.flatMap(([, fields]) => fields)
    .filter(([k]) => !shownTop.has(k) && !filled(k)).length;

  return (
    <div className="content singlewinedetail">
      <div className="mt-4 singleWinepage wd-request">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4" style={{ gap: 12 }}>
          <Button onClick={() => navigate(-1)} className="btn btn-primary d-flex align-items-center">
            <i className="tim-icons icon-minimal-left mr-2"></i>
            <span>{t("common.back")}</span>
          </Button>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
            <Button className="btn btn-info" disabled={asking} onClick={askAI}>
              {asking ? t("common.askingAI") : t("common.askAI")}
            </Button>
            <Link to={`${PATHS.WINE_DATASET_UNAPPROVED_EDIT}/${singleData.wine_dataset_request_uuid}`}>
              <Button className="wd-btn-dark d-flex align-items-center">
                <i className="tim-icons icon-pencil mr-2"></i>
                <span>{t("common.edit")}</span>
              </Button>
            </Link>
            <Button className="btn btn-success" onClick={handleApprove(singleData.wine_dataset_request_uuid)}>
              <i className="tim-icons icon-check-2 mr-2"></i>
              <span>{t("common.approve")}</span>
            </Button>
          </div>
        </div>

        <h1 className="mb-1">{attrs.name || singleData.name}</h1>
        <p className="text-muted mb-4">
          {t("requests.requestedBy")} <strong>{singleData.restaurant_name || "-"}</strong>
          {singleData.requested_at
            ? t("requests.requestedOn", { date: new Date(singleData.requested_at).toLocaleString("it-IT", {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
              }) })
            : ""}
        </p>

        {singleData.wine_dataset_request_uuid ? (
          <RequestOperatorPanel
            requestId={singleData.wine_dataset_request_uuid}
            onLinked={() => navigate(PATHS.WINE_DATASET_UNAPPROVED_LIST)}
          />
        ) : null}

        <Card className="wd-section">
          <h4 className="wd-section-title">{t("requests.receivedTitle")}</h4>
          <div className="wd-grid">
            {[...FROM_RESTAURANT, ...topOptional].map(([key]) => (
              <Field key={key} label={t(`fields.${key}`)} filled={filled(key)} suggested={false}>
                {display(key)}
              </Field>
            ))}
            <Field label={t("requests.priceOnList")} filled={!isEmpty(singleData.price)}>
              {`€ ${formatDecimalWithComma(singleData.price)}`}
            </Field>
          </div>
        </Card>

        <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
          <h3 className="mb-0">{t("requests.toFill")}</h3>
          <span className="wd-tag">{t("requests.emptyFields", { count: missingCount })}</span>
        </div>
        {TO_FILL.map(([title, fields]) => {
          const rows = fields.filter(([k]) => !shownTop.has(k));
          if (!rows.length) return null;
          return (
            <Card className="wd-section" key={title}>
              <h4 className="wd-section-title">{t(`sections.${title}`)}</h4>
              <div className="wd-grid">
                {rows.map(([key, type, unit]) => (
                  <Field
                    key={key}
                    label={t(`fields.${key}`)}
                    wide={type === "wide"}
                    filled={type === "bool" ? typeof attrs[key] === "boolean" : filled(key)}
                    suggested={suggested.includes(key) && filled(key)}
                  >
                    {display(key, type, unit)}
                  </Field>
                ))}
              </div>
            </Card>
          );
        })}

        {Array.isArray(attrs.ai_sources) && attrs.ai_sources.length ? (
          <Card className="wd-section">
            <h4 className="wd-section-title">{t("requests.sourcesTitle")}</h4>
            <ul className="mb-0">
              {attrs.ai_sources.map((src) => (
                <li key={src.url}>
                  <a href={src.url} target="_blank" rel="noopener noreferrer">
                    {src.title || src.url}
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <Card className="wd-section">
          <h4 className="wd-section-title">{t("sections.images")}</h4>
          <div className="row">
            {IMAGES.map(([key]) => (
              <div className="col-6 col-lg-3 mb-3" key={key}>
                <div className="wd-field-label">{t(`fields.${key}`)}</div>
                {attrs[key] ? (
                  <img
                    className="productImage"
                    style={{ objectFit: "cover" }}
                    src={mediaUrl(attrs[key])}
                    alt={t(`fields.${key}`)}
                  />
                ) : (
                  <div className="wd-field-value wd-field-empty">{t("requests.toUpload")}</div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UnapprovedWineDataSingle;
